import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Stack } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getCuratorSettings } from '../api/curator';
import { icons } from '../assets';
import {
  ActionFilters,
  BigButton,
  DeactivatePopup,
  GlobalFilter,
  Loader,
  ModalButtonArea,
  SharePopup,
  TopButtonArea,
} from '../common';
import AddToCollectionPopup from '../common/AddToCollectionPopup';
import { getThumbnail } from '../common/MyProductsTextureEdit/textureEditConfig';
import { PermissionArea } from '../common/PermissionArea';
import { TextureDetailsModal } from '../common/TextureDetailsModal';
import { EditTexturePopup } from '../common/Textures/EditTexturePopup';
import { ThumbnailContainerV2 } from '../common/ThumbnailContainerV2';
import { PageSearchBar } from '../common/UniversalSearchForm/UniversalSearchForm';
import ArrowDownIcon from '../components/icons/ArrowDownIcon';
// import CollectionIcon from '../components/icons/CollectionIcon';
import InfiniteScroll from '../components/InfiniteScroll/InfiniteScroll';
import MyDropzone from '../components/MyDropzone';
import { deleteUnuploadedTextures } from '../components/textureBulkUpload/UploadBulkTextureModal';
import {
  COMMON_TAGS_MODEL_TYPES,
  FILTER_BY_DELETE_OPTIONS_LIST,
  MODEL_TYPES,
} from '../constants/constants';
import { PERMISSION } from '../constants/permissionConstants';
import {
  ADMIN_MY_PRODUCTS_3DMODAL_EACH_V2,
  ADMIN_MY_PRODUCTS_TEXTURE_UPLOAD,
  MY_PRODUCTS_ROUTE,
} from '../constants/routePathConstants';
import {
  MAX_UPLOAD_HEIGHT_PIXELS,
  MAX_UPLOAD_WIDTH_PIXELS,
  MODE_UPLOAD,
} from '../constants/textureConstants';
import { useUserContext } from '../contexts/UserContext';
import {
  apiBulkDeleteTextures,
  apiBulkDeleteTexturesAdmin,
  apiBulkUploadTextures,
  apiEditTexture,
  apiGetTexture,
  apiTextureDeleteAndRestore,
  assignThisTexture,
} from '../helpers/api';
import { useScreenSettings } from '../helpers/configureHelper';
import {
  duplicateArrayRemove,
  getFormData,
  getImageDetails,
  removeSpecialCharacters,
  roundToTwo,
  inchesToCentimeter,
  totalItemCount,
  compare,
  addOneMonthAddCurrentYear,
  openFormInCaseOfNewOrgAndFreemiumUser,
  convertImagesToPNG,
} from '../helpers/jsHelper';
import { errorToast, successToast, warningToast } from '../helpers/toastHelper';
import { usePermissionsHandler, useStaticPermissions } from '../hooks/usePermissions';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTextureActions } from '../hooks/useTextureActions';
import { useTextures, useUploads } from '../hooks/useTextures';
import { Breadcrumbs, CommonGrid, TopContainer } from '../layouts';
import {
  bulkTextureUploadActions,
  selectIsBulkTextureModalExpand,
  selectIsBulkTextureModalHasData,
  selectIsBulkTextureModalOpen,
  selectIsBulkTextureProcessing,
} from '../redux/slicers/bulkTextureUpload';
import {
  selectAvailableTextures,
  selectHighestPlanOrderId,
  selectHighestPlanPriceAmount,
  selectUserActivePlanSubscriptionOrderId,
  selectUserActiveSubscriptionPlanPriceAmount,
  selectUserPlanList,
} from '../redux/slicers/subscriptionSlicer';
import {
  selectTextureDetailsModalData,
  textureDetailsModalActions,
} from '../redux/slicers/textureDetailsModalSlicer';

import { MxEvent } from '../common/Mixpanel';

import { getAppState } from '../redux/store';
import {
  navigateToAddOns,
  navigateToUpgradePlan,
} from './subscriptionComponents/subscriptionHelper';
import {
  setAllProductsUsedCount,
  setClientSubscriptionPlans,
} from '../api/subscription/subscription';
import { dispatcher } from '../helpers/projectHelper';
import { infoDialogActions } from '../redux/slicers/infoDialogSlicer';
import FilterSideModalButton from '../common/FilterSideModalButton';
import TopContainerV2 from '../layouts/TopContainerV2';
import FooterOptions from '../layouts/FooterOptions';
import { GlobalDefaultTeamMemberFilter, GlobalSearchTagsFilter } from '../common/GlobalFilter';
import { setSignupPopOpen, setSignupPopOpenedFrom } from '../redux/slicers/uiSlicer';
import { addUpdateParamsInSequence } from '../redux/slicers/filters/filterSlicer';
import { getParamsFromFilterBy } from '../helpers/urlHelper';
import { useInfoDialogaActions } from '../hooks/useInfoDialogaActions';
import { TrashV2Icon, WarningContentInfoIcon } from '../assets/icons';
import { isAdminOrOwner } from '../helpers/userHelper';
import { RestoreIcon } from '../components/icons/RestoreIcon';
import useCurrentOrganizationInfo from '../hooks/useCurrentOrganizationInfo';
import { addFiles } from '../redux/slicers/appSlicer';
import { GenerateContentModal } from '../components/popups/GenerateContentModal';
import PaymentDoneIcon from '../components/icons/PaymentDone';
import { selectFetchTextures } from '../redux/slicers/productV2Slicer';
import DownloadIcon from '../components/icons/DownloadIcon';

const {
  CollectionIcon,
  EditIcon,
  UploadIcon,
  UploadIconAlt,
  MoveToTextureIcon,
  FileFolder,
  UploadXLXS,
  Close,
  File,
} = icons;

const closeAndGoToUpgradePlan = () => {
  dispatcher(infoDialogActions.closeInfoDialog());
  navigateToUpgradePlan({ browsePlan: true, newTab: true });
};

const closeAndGoToAddons = () => {
  dispatcher(infoDialogActions.closeInfoDialog());
  navigateToAddOns({ newTab: true });
};

export const showDialogForTextureLimitExceeds = (isGuestLogin, hideNewPlans, isFreemiumUser) => {
  if (isGuestLogin) {
    dispatcher(setSignupPopOpen(true));
    dispatcher(setSignupPopOpenedFrom('texture-limit-modal'));
    return;
  }
  const availableTextures = selectAvailableTextures(getAppState());
  // const highestPlanOrder = selectHighestPlanOrderId(getAppState());
  const highestPlanPriceAmount = selectHighestPlanPriceAmount(getAppState());
  // const activePlanOrder = selectUserActivePlanSubscriptionOrderId(getAppState());
  const activePlanPriceAmount = selectUserActiveSubscriptionPlanPriceAmount(getAppState());

  const showUpgradeButton = highestPlanPriceAmount > activePlanPriceAmount; // highestPlanOrder > activePlanOrder;

  const description =
    availableTextures === 0 && !availableTextures
      ? 'You have reached your texture upload limit.'
      : `You can only upload ${availableTextures} new texture${
          availableTextures > 1 ? 's' : ''
        } to your organization.`;
  const onButtonClick = () => {
    if (showUpgradeButton) {
      if (hideNewPlans || isFreemiumUser) {
        // open enterprise form in case of freemium user whose org is created after 10th Feb 2025
        dispatcher(infoDialogActions.closeInfoDialog());
        setTimeout(() => {
          openFormInCaseOfNewOrgAndFreemiumUser();
        }, 0);
      } else {
        closeAndGoToUpgradePlan();
      }
    }
  };

  const onButtonClick2 = () => {
    if (hideNewPlans || isFreemiumUser) {
      // open enterprise form in case of freemium user whose org is created after 10th Feb 2025
      dispatcher(infoDialogActions.closeInfoDialog());
      setTimeout(() => {
        openFormInCaseOfNewOrgAndFreemiumUser();
      }, 0);
    } else {
      closeAndGoToAddons();
    }
  };
  dispatcher(
    infoDialogActions.openInfoDialog({
      title: 'Exceeds Limit.',
      description: description,
      buttonText: showUpgradeButton ? 'Upgrade your plan' : '',
      onButtonClick,
      buttonText2: 'Buy more textures',
      onButtonClick2,
      addOrSeparator: showUpgradeButton,
      btnContainerClass: 'align-items-center',
      dispatch: dispatcher,
    })
  );
};

// TODO: move it to separate component
export const UploadTextureModal = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOldSubscriptionUser, isPremiumUser, isGuestLogin, isGuestUser } = useUserContext();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [btnTitle, setBtnTitle] = useState('Import');
  const availableTextures = useSelector(selectAvailableTextures);
  const { isUnlimitedTextures, hideNewPlans, isFreemiumUser } = useCurrentOrganizationInfo();
  const [allSelectedFiles, setAllSelectedFiles] = useState([]);
  // const [uploadedTextures, setUploadedTextures] = useState([])
  const { hasPermission } = useStaticPermissions();
  const canAccessV2Page = isGuestUser() || hasPermission(PERMISSION.PRODUCT_PAGE_V2);

  const handleClose = () => {
    setSelectedFiles([]);
    setAllSelectedFiles([]);
    props?.setShowModal?.(false);
  };

  useEffect(() => {
    dispatcher(setAllProductsUsedCount());
    dispatcher(bulkTextureUploadActions.closeModal());
    return () => handleClose();
  }, []);

  async function isFilePresent(file) {
    if (selectedFiles.length === 0) return Promise.resolve(false);

    const res = await Promise.all(selectedFiles.map((sf) => compare(sf.file, file)));
    const x = res.every((r) => r === false);
    return !x;
  }

  const handleFileChange = async (files) => {
    dispatcher(setClientSubscriptionPlans());

    // Convert non-JPEG/PNG files to PNG
    const convertedFiles = await Promise.all(
      files.map(async (file) => {
        const fileType = file.type.toLowerCase();
        // Check if file is not JPEG or PNG
        if (fileType !== 'image/jpeg' && fileType !== 'image/png') {
          try {
            const convertedFile = await convertImagesToPNG(file);
            return convertedFile;
          } catch (error) {
            errorToast(`Failed to convert ${file.name}: ${error}`);
            return null;
          }
        }
        return file;
      })
    );

    // Filter out any null values from failed conversions
    const validFiles = convertedFiles.filter((file) => file !== null);

    if (validFiles.length === 0) {
      return;
    }
    // const noMainSubscriptionData = userPlans?.length === 0;
    // const doNotRestrictToUpload =
    //   isOldSubscriptionUser() || (isPremiumUser() && noMainSubscriptionData);
    if (!isUnlimitedTextures && validFiles.length > availableTextures) {
      showDialogForTextureLimitExceeds(isGuestLogin, hideNewPlans, isFreemiumUser);
      return;
    }

    setAllSelectedFiles((prevFiles) =>
      duplicateArrayRemove([...prevFiles, ...validFiles.map((a) => a?.name)])
    );
    validFiles.forEach((file) => {
      isFilePresent(file).then((res) => {
        if (res === true) {
          errorToast(`This file named "${file?.name}" has already been uploaded`);
          return;
        }

        var fileName = removeSpecialCharacters(file.name);
        const src = URL.createObjectURL(file);
        getImageDetails(src)
          .then((res) => {
            const { width, height, dpi, widthInches, heightInches } = res;
            if (width > MAX_UPLOAD_WIDTH_PIXELS || height > MAX_UPLOAD_HEIGHT_PIXELS) {
              URL.revokeObjectURL(src);
              errorToast('The texture you have uploaded is above 16k resolution');
              return;
            }
            const _width = roundToTwo(inchesToCentimeter(widthInches));
            const _height = roundToTwo(inchesToCentimeter(heightInches));

            getThumbnail(file).then((tFile) => {
              const uploadFile = {
                id: `id-${new Date().getTime()}`,
                name: fileName,
                sku_id: '',
                width: _width,
                height: _height,
                rotation: 0,
                description: '',
                filteroptions: [],
                file: file,
                texture_file: src,
                thumbnail: tFile,
                fileWithExtension: file?.name,
              };
              setSelectedFiles((prevFiles) => [uploadFile, ...prevFiles]);
            });
          })
          .catch(() => {
            const errorMessage =
              files.length > 1
                ? 'Some of the textures you are trying to upload are above 16k resolution'
                : 'The texture you are trying to upload is above 16k resolution';
            errorToast(errorMessage);
          });
      });
    });
  };

  const handleFileDelete = (fileId) => {
    const updatedFiles = selectedFiles.filter((x) => x.id !== fileId);
    const fileToDelete = selectedFiles.find((x) => x.id === fileId);
    setSelectedFiles(updatedFiles);
    setAllSelectedFiles(allSelectedFiles.filter((a) => a !== fileToDelete?.fileWithExtension));
  };

  let uploadedTextures = [];
  const handleFileImport = async () => {
    if (props?.handleImportTextures && canAccessV2Page) {
      props.handleImportTextures(selectedFiles);
      handleClose();
      dispatcher(bulkTextureUploadActions.closeModal());
      return;
    }
    setBtnTitle('Please wait!!!');

    const texturesForUpload = selectedFiles.map(({ id, name, width, height, rotation, file }) => {
      return {
        name,
        width,
        height,
        rotation,
        is_map: false,
        texture_file_url_key: file.name,
      };
    });

    const formData = getFormData(texturesForUpload);
    await apiBulkUploadTextures(formData, null, null)
      .then((res) => {
        if (res && res?.status) {
          setBtnTitle('Redirecting...');

          const updatedData = selectedFiles.map((a) => {
            const { id, is_map, presigned_data, presigned_url } = res?.data.find(
              (b) => b.name === a.name
            );

            return {
              ...a,
              id: id,
              is_map: is_map,
              is_uploaded: false,
              version: 1,
              presigned_data,
              presigned_url,
            };
          });

          uploadedTextures = [...updatedData];
        }
      })
      .catch((error) => {
        if (error?.message?.toLowerCase()?.includes('limit has been reached')) {
          showDialogForTextureLimitExceeds(isGuestLogin, hideNewPlans, isFreemiumUser);
        } else {
          errorToast(error?.message);
        }
      })
      .finally(() => {
        setBtnTitle('Upload');
      });

    const r = [...selectedFiles, ...uploadedTextures];

    dispatcher(setClientSubscriptionPlans());

    if (uploadedTextures?.length) {
      navigate(ADMIN_MY_PRODUCTS_TEXTURE_UPLOAD, {
        state: {
          textures: [...(location?.state?.textures || []), ...uploadedTextures],
          mode: MODE_UPLOAD,
        },
        replace: true,
      });
      uploadedTextures = [];
      // setSelectedFiles([]);
      if (typeof props?.setShowModal === 'function') props?.setShowModal(false);
    }
  };

  const checkForDisable = () => {
    if (selectedFiles.length !== allSelectedFiles.length) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <ModalButtonArea
      {...props}
      size="md"
      centered={true}
      bigButton={true}
      variant="link"
      buttonTitle={props.buttonTitle}
      iconBefore={props.iconBefore}
      onClose={handleClose}
      checkForPermission="UPLOAD_TEXTURE"
      className="upload-texture-modal"
      disabled={props.disableButton}
      backdropClassName="overlay-dark"
      headingLeft
      {...(selectedFiles?.length !== 0 && { backdrop: 'static' })}
      title={
        <>
          <h4>Upload textures</h4>
        </>
      }
      body={
        <div>
          <MyDropzone
            v2UploadTextures
            subTitle="(.jpg, .png files supported. Max resolution 16k) "
            onDrop={handleFileChange}
          />
          {selectedFiles.length > 0 && (
            <Stack>
              <div
                style={{
                  position: 'relative',
                  maxHeight: 225,
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  marginTop: '1.5rem',
                  marginBottom: '2rem',
                }}>
                <table className="bordered">
                  <tbody>
                    {selectedFiles.map((file, index) => (
                      <tr key={index}>
                        <td>
                          <span
                            style={{
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                            }}>
                            <File className="me-2" /> {file.name}
                          </span>
                          <span
                            onClick={() => handleFileDelete(file.id)}
                            className="close-upload-file">
                            <Close />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="file-selected-upload">
                <div>
                  <p>{selectedFiles ? selectedFiles.length : 0} files selected</p>
                </div>
                <BigButton
                  className="lf-btn-1"
                  buttonTitle={btnTitle}
                  onClick={handleFileImport}
                  disabled={checkForDisable()}
                />
              </div>
            </Stack>
          )}
          {checkForDisable() && (
            <div
              className="d-flex justify-content-center"
              style={{ position: 'relative', gap: '20px', paddingTop: '4px' }}>
              <span>Loading files</span>{' '}
              <Loader loading size={16} thickness={1} position={'relative'} />
            </div>
          )}
        </div>
      }
    />
  );
};

const MyProductsTextures = ({
  cardType = 'textures',
  breadcrumbs,
  title = 'Textures',
  pageTitle = 'Textures - My Products',
  apiConfig,
  type = 'textures',
  modelType = MODEL_TYPES.TEXTURE,
  useGlobalSearch,
  noDataText = modelType === MODEL_TYPES.TEXTURE
    ? 'Your textures live here!'
    : 'You do not have any custom map available.',
  cardProps,
  membersFilterDefaultSelected = '', // 'team',
  hideBreadcrumbs = false,
  designV2,
  hideStickyHeader = null,
}) => {
  usePageTitle(pageTitle);
  const dispatch = useDispatch();
  const userCtx = useUserContext();
  const { isFreemiumOrSelfServeUser, isUnlimitedTextures } = useCurrentOrganizationInfo();
  const { isGuestUser } = userCtx;
  const currentUser = userCtx && userCtx.currentUser;
  const userCurrentOrg = currentUser && currentUser.organization;
  const currentMemberId = currentUser && currentUser.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const textureId = searchParams.get('texture_id');

  const { handlePermissionEvent } = usePermissionsHandler();
  const { openInfoDialog, closeInfoDialog } = useInfoDialogaActions();

  const isTextureUploadHasData = useSelector(selectIsBulkTextureModalHasData);
  const isTextureUploadProcessing = useSelector(selectIsBulkTextureProcessing);
  const isTextureUploadModalOpen = useSelector(selectIsBulkTextureModalOpen);

  const [showModal, setShowModal] = useState(false);
  const [showApplyTextureModal, setShowApplyTextureModal] = useState();
  const textureUploaded = useSelector(selectFetchTextures);
  const { hasPermission } = useStaticPermissions();

  const canAccessV2Page = isGuestUser() || hasPermission(PERMISSION.PRODUCT_PAGE_V2);
  const canAssignTexture = hasPermission(PERMISSION.CAN_ASSIGN_TEXTURE);

  useEffect(() => dispatch(setAllProductsUsedCount()), []);

  useEffect(async () => {
    if (textureId) {
      const res = await apiGetTexture({ id: textureId });
      if (res) {
        dispatch(textureDetailsModalActions.show(res));
      }
    }
  }, [textureId]);

  useEffect(() => {
    if (textureUploaded) {
      refetchList();
    }
  }, [textureUploaded]);

  const pageSettings = useScreenSettings(cardType);
  const { sortOptions, showFavoriteIcon, defaultSortOption, showTeamMemberFilter } = pageSettings;

  const requestParams = useMemo(
    () => ({
      created_by: '', // currentMemberId,
      is_map: false,
      is_guest_user: isGuestUser(),
      ...apiConfig?.requestParams,
    }),
    [currentMemberId, apiConfig?.requestParams]
  );

  const useDataHook = modelType === MODEL_TYPES.UPLOADS ? useUploads : useTextures; // temp solution
  const {
    list,
    loading,
    fetchMoreData,
    hasMore,
    total,
    hasData,

    selected,
    anySelected,
    allSelected,
    selectedList,
    sortBy,
    filterBy,
    onSortChange,
    toggleAll,
    toggleItem,
    resetSelection,
    addToCollection,
    refetchList,
    search,
    onSearchChange,
    onFilterByChange,
  } = useDataHook({
    requestParams,
    defaultBrands: userCurrentOrg,
    defaultSort: defaultSortOption,
    useGlobalSearch,
    modelType,
    useOptimizedAPI: true,
  });

  const isMattressTextureSelected = selectedList.every((ins) => ins?.is_mattress_texture);

  const {
    loading: isLoading,
    handleEditClick,
    handleDownloadClick,
    handleUploadEditClick,
  } = useTextureActions({
    list,
    modelType,
    isCustomMapsEdit: type === 'uploads' && isMattressTextureSelected,
  });

  const handleAssignTextures = async (list) => {
    const allIds = list.map((data) => data.id);
    await assignThisTexture(allIds);
  };

  const handleDeleteGuestUser = (data) => {
    let deleteItem = data;
    deleteItem = deleteItem.filter((val) => val?.owner_organization === currentUser?.organization);
    const isSingleDelete = deleteItem?.length === 1;
    if (!deleteItem?.length) return;
    openInfoDialog({
      topIcon: <WarningContentInfoIcon />,
      title: isSingleDelete
        ? 'Are you sure you want to delete this texture?'
        : 'Are you sure you want to proceed with these textures?',
      description: (
        <p>
          {`The selected texture${
            isSingleDelete ? '' : 's'
          } may be linked to certain items(projects, props, renders,
                    etc)`}
        </p>
      ),
      titleStyle: { textAlign: 'start' },
      cancelButtonText: 'Cancel',
      buttonText: 'Confirm',
      size: 'lg',
      onButtonClick: async () => {
        const formData = new FormData();
        deleteItem?.map(({ id }) => {
          formData.append('id', id);
        });
        await apiBulkDeleteTexturesAdmin(formData).then((res) => {
          if (res?.status) {
            refetchList?.();
            successToast(res?.message || `Your texture${isSingleDelete ? '' : 's'} deleted successfully`);
          } else {
            errorToast(res?.message || 'Something went wrong. Please try again.');
          }
        });
      },
    });
  };

  const deleteBtn = [
    {
      element: BigButton,
      bigBtnClass: 'lf-btn-2',
      variant: 'secondary',
      buttonTitle: 'Delete',
      iconBefore: <TrashV2Icon />,
      onClick: () => handleDeleteGuestUser(selectedList)
    },
  ];

  // upload buttons
  const uploadsTopButtons = [
    {
      element: DeactivatePopup,
      bigButton: true,
      bigBtnClass: 'lf-btn-2',
      isPlural: true,
      item: cardType,
      actionWord: 'delete',
      deleteAction: apiBulkDeleteTextures,
      onSuccess: refetchList,
    },
    {
      // Import movetotexture icon here instead of MoveToTextureIcon
      element: BigButton,
      bigBtnClass: 'lf-btn-2',
      variant: 'secondary',
      buttonTitle: 'Move to textures',
      iconBefore: <MoveToTextureIcon />,
      onClick: () => handleUploadEditClick(selectedList),
    },
    isMattressTextureSelected && {
      element: BigButton,
      bigBtnClass: 'lf-btn-2',
      variant: 'secondary',
      buttonTitle: 'Edit',
      iconBefore: <EditIcon />,
      onClick: () => handleEditClick(selectedList),
    },
    {
      element: BigButton,
      bigBtnClass: 'lf-btn-1',
      buttonTitle: 'Download',
      iconBefore: <DownloadIcon />,
      onClick: () => handleDownloadClick(selectedList),
      isLoading: isLoading,
    },
    // {
    //   element: canAssignTexture && BigButton,
    //   bigBtnClass: 'lf-btn-2',
    //   variant: 'secondary',
    //   buttonTitle: 'Assign Textures',
    //   iconBefore: <MoveToTextureIcon />,
    //   onClick: () => handleAssignTextures(selectedList),
    // },
  ].filter((val) => Boolean(val));

  // textures top buttons
  const texturesTopButtons = [
    {
      element: DeactivatePopup,
      bigButton: true,
      bigBtnClass: 'lf-btn-3',
      isPlural: true,
      item: cardType,
      actionWord: 'delete',
      deleteAction: apiBulkDeleteTextures,
      onSuccess: refetchList,
    },
    {
      element: BigButton,
      bigBtnClass: 'lf-btn-2',
      variant: 'secondary',
      buttonTitle: 'Edit',
      iconBefore: <EditIcon />,
      onClick: () => handleEditClick(selectedList),
    },
    {
      element: BigButton,
      bigBtnClass: 'lf-btn-2',
      iconBefore: <CollectionIcon />,
      buttonTitle: 'Add To Collection',
      onClick: addToCollection,
    },
    {
      element: SharePopup,
      bigButton: true,
      bigBtnClass: 'lf-btn-2',
      onSuccess: refetchList,
      modelType: MODEL_TYPES.TEXTURE,
      selectedList,
    },
    // {
    //   element: canAssignTexture && BigButton,
    //   bigBtnClass: 'lf-btn-2',
    //   variant: 'secondary',
    //   buttonTitle: 'Assign Textures',
    //   iconBefore: <MoveToTextureIcon />,
    //   onClick: () => handleAssignTextures(selectedList),
    // },
    {
      element: BigButton,
      bigBtnClass: 'lf-btn-1',
      buttonTitle: 'Download',
      iconBefore: <DownloadIcon />,
      onClick: () => handleDownloadClick(selectedList),
      isLoading: isLoading,
    },
  ];

  const handleDelete = (data) => {
    let deleteItem = data;
    const restrictedItem = !isAdminOrOwner(currentUser)
      ? data?.filter(({ member_details }) => member_details?.member_id !== currentUser.id)
      : [];
    if (!isAdminOrOwner(currentUser) && restrictedItem?.length) {
      deleteItem = data?.filter(
        ({ member_details }) => member_details?.member_id === currentUser.id
      );
      if (!deleteItem?.length) {
        warningToast('You do not have ownership to delete this item.');
        return;
      }
    }
    const oldEntry = deleteItem?.filter(({ is_special_case_texture }) => is_special_case_texture);
    const newEntry = deleteItem?.filter(({ is_special_case_texture }) => !is_special_case_texture);

    const handleOldPopup = () => {
      const message = 'Your items can be restored from the Trash area.';
      const formData = new FormData();
      oldEntry.forEach((list) => {
        formData.append('id', list.id);
      });
      openInfoDialog({
        topIcon: <WarningContentInfoIcon />,
        title: 'Are you sure you want to delete this texture?',
        secondLineDescription: message,
        titleStyle: { textAlign: 'start' },
        buttonText: 'Delete',
        size: 'lg',
        onButtonClick: () =>
          apiBulkDeleteTextures(formData, null, null, null, () => {
            refetchList();
            closeInfoDialog();
          }),
        cancelButtonText: 'Cancel',
        onCancelClick: () => closeInfoDialog(),
      });
    };

    if (!newEntry?.length && oldEntry?.length) {
      handleOldPopup();
      return;
    }

    const isSingleDelete = deleteItem?.length === 1;
    // const date =
    //   isSingleDelete && deleteItem?.[0]?.delete_expired_date
    //     ? addOneMonthAddCurrentYear(deleteItem?.[0]?.delete_expired_date)
    //     : null;
    const date = null;

    const hasInactiveItem = deleteItem.some(
      ({ is_customer_product_expired, is_mattress_expired, is_mattress_texture }) =>
        is_mattress_texture ? is_mattress_expired : is_customer_product_expired
    );
    const handleSuccessMessage = (restrictedItem) => {
      const message = restrictedItem?.length
        ? "Some items were not deleted because you don't have the necessary ownership permissions to delete them."
        : '';
      openInfoDialog({
        topIcon: <PaymentDoneIcon />,
        title: 'Your delete request has been submitted.',
        description: date ? (
          <>
            {`All the items (projects, props, renders, etc.) using this texture will no longer be accessible after`}{' '}
            <strong>
              <b>{date}</b>
            </strong>
            .
          </>
        ) : (
          `All items (projects, props, renders, etc.) using ${
            isSingleDelete ? 'this texture' : 'these textures'
          } will no longer be accessible after their respective deletion dates.`
        ),
        secondLineDescription: message,
        titleStyle: { textAlign: 'start' },
        buttonText: 'Okay',
        size: 'lg',
        onButtonClick: () => closeInfoDialog(),
      });
    };
    const callRemoveOldItems = async () => {
      if (oldEntry?.length) {
        const formData = new FormData();
        oldEntry.forEach((list) => {
          formData.append('id', list.id);
        });
        await apiBulkDeleteTextures(formData, null, null, null);
      }
    };
    openInfoDialog({
      topIcon: <WarningContentInfoIcon />,
      title: isSingleDelete
        ? 'Are you sure you want to delete this texture?'
        : 'Are you sure you want to proceed with these textures?',
      description: (
        <>
          {hasInactiveItem && (
            <p>
              {`You have selected some inactive Textures. These textures will be deleted permanently. This action cannot be undone.`}
            </p>
          )}

          {date ? (
            <>
              {`This texture maybe linked to certain items (projects, props, renders, etc.). You won’t be able to access these items after`}{' '}
              <strong>
                <b>{date}</b>
              </strong>
              .
            </>
          ) : (
            <>
              <p>
                {`The selected texture${
                  isSingleDelete ? '' : 's'
                } may be linked to certain items(projects, props, renders,
                    etc). After the respective subscription period ends, you will no longer have
                    access to these linked items.`}
              </p>

              <p>
                {`To retain access, you can restore ${
                  isSingleDelete ? 'this texture' : 'these textures'
                }  before the respective subscription
                    period ends.`}
              </p>
            </>
          )}
        </>
      ),
      secondLineDescription:
        date && isSingleDelete
          ? `You can restore the texture before this date to retain access.`
          : '',
      titleStyle: { textAlign: 'start' },
      cancelButtonText: 'Cancel',
      buttonText: 'Confirm',
      size: 'lg',
      onButtonClick: async () => {
        const formData = new FormData();
        formData.append('is_delete_on_expire', true);
        formData.append('texture_ids', newEntry?.map(({ id }) => id)?.join(','));
        await callRemoveOldItems();
        await apiTextureDeleteAndRestore(
          formData
          // `Your ${!isSingleDelete ? 'textures' : 'texture'} deleted successfully`
        )
          .then((res) => {
            refetchList?.();
            handleSuccessMessage(restrictedItem);
          })
          .catch((error) => {});
      },
    });
  };

  const handleRestore = (data) => {
    let deleteItem = data;
    const restrictedItem = !isAdminOrOwner(currentUser)
      ? data?.filter(({ member_details }) => member_details?.member_id !== currentUser.id)
      : [];
    if (!isAdminOrOwner(currentUser) && restrictedItem?.length) {
      deleteItem = data?.filter(
        ({ member_details }) => member_details?.member_id === currentUser.id
      );
      if (!deleteItem?.length) {
        warningToast('You do not have ownership to restore this item.');
        return;
      }
    }
    const message = restrictedItem?.length
      ? "Some items were not restored because you don't have the necessary ownership permissions to restore them."
      : '';
    const isSingleRestore = deleteItem?.length === 1;
    openInfoDialog({
      topIcon: <WarningContentInfoIcon />,
      title: isSingleRestore
        ? 'Are you sure you want to restore this texture?'
        : 'Are you sure you want to restore these textures?',
      description: `${
        isSingleRestore ? 'This texture' : 'These textures'
      } maybe linked to certain items (projects, props, renders etc.). Restoring it will allow you to use these items without any restrictions.`,
      secondLineDescription: message,
      cancelButtonText: 'Cancel',
      buttonText: 'Confirm',
      size: 'lg',
      titleStyle: { textAlign: 'start' },
      onButtonClick: async () => {
        const formData = new FormData();
        formData.append('is_delete_on_expire', false);
        formData.append('texture_ids', deleteItem?.map(({ id }) => id)?.join(','));
        const res = await apiTextureDeleteAndRestore(
          formData,
          `Your ${!isSingleRestore ? 'textures' : 'texture'} restored successfully`
        );
        if (res?.status) {
          refetchList?.();
        }
      },
      okBtnColor: null,
      cancelBtnColor: null,
      okBtnClassName: null,
    });
  };

  const texturesTopButtonsNewUser = [
    {
      element: BigButton,
      bigBtnClass: 'lf-btn-2',
      variant: 'secondary',
      buttonTitle: 'Delete',
      iconBefore: <TrashV2Icon />,
      onClick: () =>
        handleDelete(selectedList?.filter(({ is_delete_on_expire }) => !is_delete_on_expire)),
    },
    {
      element: BigButton,
      bigBtnClass: 'lf-btn-2',
      variant: 'secondary',
      buttonTitle: 'Restore',
      iconBefore: <RestoreIcon />,
      onClick: () =>
        handleRestore(selectedList?.filter(({ is_delete_on_expire }) => is_delete_on_expire)),
    },
  ];

  const handleFilterBtn = () => {
    const isDeletedRequest = selectedList?.some(({ is_delete_on_expire }) => !is_delete_on_expire);
    const isRestoredRequest = selectedList?.some(({ is_delete_on_expire }) => is_delete_on_expire);
    let newList = [];
    if (isRestoredRequest && isDeletedRequest) {
      newList.push(...texturesTopButtonsNewUser);
    }
    if (isDeletedRequest && !isRestoredRequest) {
      newList.push(...texturesTopButtonsNewUser.slice(0, 1));
    }
    if (isRestoredRequest && !isDeletedRequest) {
      newList.push(...texturesTopButtonsNewUser.slice(1));
    }
    newList.push(...texturesTopButtons.slice(1));
    return newList;
  };

  // const topButtons =
  //   type === 'uploads'
  //     ? uploadsTopButtons
  //     : isFreemiumOrSelfServeUser
  //     ? handleFilterBtn()
  //     : texturesTopButtons;

  const topButtons =
    type === 'uploads'
      ? uploadsTopButtons
      : isUnlimitedTextures
      ? texturesTopButtons
      : handleFilterBtn();

  breadcrumbs = hideBreadcrumbs
    ? []
    : breadcrumbs || [
        {
          link: MY_PRODUCTS_ROUTE,
          label: 'My products',
          mx_event: MxEvent.VISITED_MY_PRODUCTS,
        },
        {
          label: `Textures`,
        },
      ];

  useEffect(() => {
    deleteUnuploadedTextures();
    let timeId;
    if (isTextureUploadModalOpen && isTextureUploadHasData && !isTextureUploadProcessing) {
      timeId = setTimeout(() => {
        !loading && refetchList?.();
      }, 1500);
    }

    return () => {
      if (timeId) clearTimeout(timeId);
    };
  }, [isTextureUploadProcessing]);

  const openBulkTextureModal = async () => {
    const expand = selectIsBulkTextureModalExpand(getAppState());

    if (isTextureUploadModalOpen) {
      if (isTextureUploadProcessing) {
        warningToast(
          'The textures are in processing. Please wait or close it first to add new textures.'
        );
        if (!expand) dispatch(bulkTextureUploadActions.setExpand(!expand));
      } else {
        dispatch(bulkTextureUploadActions.closeModal());
        setTimeout(async () => {
          await handlePermissionEvent(PERMISSION.UPLOAD_TEXTURE, async (e) => {
            dispatch(bulkTextureUploadActions.openModal());
            userCtx?.updateUserData();
          })();
        }, 100);
      }
    } else {
      await handlePermissionEvent(PERMISSION.UPLOAD_TEXTURE, async (e) => {
        dispatch(bulkTextureUploadActions.openModal());
        userCtx?.updateUserData();
      })();
    }
  };

  const handleFilterByChange = (value) => {
    dispatch(addUpdateParamsInSequence({ filterBy: getParamsFromFilterBy(value) }));
    onFilterByChange(value);
  };

  const handleImportTextures = (files) => {
    const __files = {};
    files?.forEach(({ id, file }) => {
      file.id = id;
      __files[id] = file;
    });
    dispatch(addFiles({ files: __files, texture: true }));
    setShowModal(false);
  };

  const handleShowUploadBtn = (isBlackBtn) => {
    if (useGlobalSearch) return;
    return (
      <TopButtonArea
        hasData={hasData}
        customClassName="upload-btn-dropdown"
        item={cardType}
        addNewButtonArea={
          type === 'uploads' || isGuestUser() ? null : (
            <PermissionArea
              name={PERMISSION.UPLOAD_BULK_TEXTURE}
              elseChildren={
                <UploadTextureModal
                  bigBtnClass={`lf-btn-1 ${isBlackBtn ? 'keep-text' : ''}`}
                  buttonTitle="Upload Textures"
                  iconBefore={<UploadIcon />}
                  handleImportTextures={handleImportTextures}
                  showModal={showModal}
                  setShowModal={setShowModal}
                />
              }>
              <Dropdown align="end">
                <Dropdown.Toggle
                  as={Button}
                  className="bigButton btn-warning btn-lg-1 font16 uploadBtn"
                  variant="warning"
                  title={'Upload your textures'}>
                  <UploadIconAlt /> Upload <ArrowDownIcon style={{ width: 18 }} />
                </Dropdown.Toggle>
                <Dropdown.Menu className="mt-2">
                  <div className="bulk-upload-dropdown">
                    <UploadTextureModal
                      buttonTitle="Upload Files"
                      bigBtnClass="keep-text"
                      iconBefore={<FileFolder />}
                      handleImportTextures={handleImportTextures}
                      showModal={showModal}
                      setShowModal={setShowModal}
                    />
                    <div
                      className="upload-texture-modal bigButton keep-text font16 btn btn-link btn-md"
                      onClick={openBulkTextureModal}>
                      <UploadXLXS /> <span>Upload with XLSX</span>
                    </div>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </PermissionArea>
          )
        }
        anySelected={anySelected}
        selectedList={selectedList}
      />
    );
  };

  const NoDataFound = () => {
    return (
      <div className="w-100 m-auto d-flex flex-column justify-content-center align-items-center">
        <h2>{noDataText}</h2>
        {modelType === MODEL_TYPES.TEXTURE && (
          <>
            <p>All the textures that you upload or get created from Imagine</p>
            <p>will be available here.</p>
            {handleShowUploadBtn(true)}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {!useGlobalSearch && <Breadcrumbs fullWidth list={breadcrumbs} />}
      <CommonGrid
        sticky={hideStickyHeader ? false : true}
        className={(anySelected || allSelected) && designV2 ? 'show-action-footer' : ''}
        noDataText={
          search ? `You don't have any ${cardType} with "${search}" name.` : NoDataFound()
        }
        {...(designV2
          ? {
              topContainer: (
                <TopContainerV2
                  subClassName={
                    modelType === MODEL_TYPES.UPLOADS
                      ? 'smaller-width mycustommaps-topContainerV2 myProductSection'
                      : 'smaller-width mytextures-topContainerV2 myProductSection'
                  }
                  globalFilter={
                    !useGlobalSearch && (
                      <div>
                        <GlobalFilter designV2 />
                        <GlobalSearchTagsFilter designV2={designV2} />
                        <GlobalDefaultTeamMemberFilter designV2 />
                      </div>
                    )
                  }>
                  <>
                    {!useGlobalSearch && (
                      <>
                        <div className="totalCount">{totalItemCount(total)}</div>
                        <PageSearchBar
                          designV2
                          model={COMMON_TAGS_MODEL_TYPES.TEXTURE}
                          onSearchChange={onSearchChange}
                          search={search}
                        />
                        <FilterSideModalButton showTeamMemberFilter={showTeamMemberFilter} />
                        <ActionFilters
                          loading={loading}
                          {...(isFreemiumOrSelfServeUser && {
                            filterProps: {
                              value: filterBy,
                              data: FILTER_BY_DELETE_OPTIONS_LIST,
                              onChange: handleFilterByChange,
                              containerClass: 'top-filter',
                            },
                          })}
                          sortProps={
                            // (loading || list?.length > 0) &&
                            {
                              value: sortBy,
                              data: sortOptions,
                              onChange: onSortChange,
                              containerClass: 'top-filter',
                            }
                          }
                          showTeamMemberFilter={false}
                          membersFilterDefaultSelected={membersFilterDefaultSelected}
                        />
                      </>
                    )}
                    {!useGlobalSearch && (
                      <TopButtonArea
                        hasData={hasData}
                        customClassName="upload-btn-dropdown"
                        item={cardType}
                        addNewButtonArea={
                          type === 'uploads' || isGuestUser() ? null : (
                            <PermissionArea
                              name={PERMISSION.UPLOAD_BULK_TEXTURE}
                              elseChildren={
                                <UploadTextureModal
                                  bigBtnClass="lf-btn-1"
                                  buttonTitle="Upload Textures"
                                  iconBefore={<UploadIcon />}
                                  handleImportTextures={handleImportTextures}
                                  showModal={showModal}
                                  setShowModal={setShowModal}
                                />
                              }>
                              <Dropdown align="end">
                                <Dropdown.Toggle
                                  as={Button}
                                  className="bigButton btn-warning btn-lg-1 font16 uploadBtn"
                                  variant="warning"
                                  title={'Upload your textures'}>
                                  <UploadIconAlt /> Upload <ArrowDownIcon style={{ width: 18 }} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="mt-2">
                                  <div className="bulk-upload-dropdown">
                                    <UploadTextureModal
                                      buttonTitle="Upload Files"
                                      bigBtnClass="keep-text"
                                      iconBefore={<FileFolder />}
                                      handleImportTextures={handleImportTextures}
                                      showModal={showModal}
                                      setShowModal={setShowModal}
                                    />
                                    <div
                                      className="upload-texture-modal bigButton keep-text font16 btn btn-link btn-md"
                                      onClick={openBulkTextureModal}>
                                      <UploadXLXS /> <span>Upload with XLSX</span>
                                    </div>
                                  </div>
                                </Dropdown.Menu>
                              </Dropdown>
                            </PermissionArea>
                          )
                        }
                        anySelected={anySelected}
                        selectedList={selectedList}
                      />
                    )}
                  </>
                </TopContainerV2>
              ),
            }
          : {
              topContainer: (
                <TopContainer
                  title={
                    !useGlobalSearch && (
                      <>
                        {title} {total > 0 && <small>({total})</small>}
                        {search !== '' && (
                          <small style={{ textTransform: 'lowercase' }}>
                            &nbsp;{`Searched by: ${search}`}
                          </small>
                        )}
                      </>
                    )
                  }
                  item={cardType}
                  hasData={hasData}
                  buttonArea={
                    <div style={{ display: 'flex' }}>
                      <>
                        <TopButtonArea
                          hasData={hasData}
                          item={cardType}
                          topButtonList={isGuestUser() ? deleteBtn : topButtons}
                          addNewButtonArea={
                            !useGlobalSearch &&
                            (type === 'uploads' || isGuestUser() ? null : (
                              <PermissionArea
                                name={PERMISSION.UPLOAD_BULK_TEXTURE}
                                elseChildren={
                                  <UploadTextureModal
                                    bigBtnClass="lf-btn-1"
                                    buttonTitle="Upload Textures"
                                    iconBefore={<UploadIcon />}
                                  />
                                }>
                                <Dropdown align="end">
                                  <Dropdown.Toggle
                                    as={Button}
                                    className="bigButton btn-warning btn-lg-1 font16 uploadBtn"
                                    variant="warning"
                                    title={'Upload your textures'}>
                                    <UploadIconAlt /> Upload <ArrowDownIcon style={{ width: 18 }} />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu className="mt-2">
                                    <div className="bulk-upload-dropdown">
                                      <UploadTextureModal
                                        buttonTitle="Upload Files"
                                        bigBtnClass="keep-text"
                                        iconBefore={<FileFolder />}
                                      />
                                      <div
                                        className="upload-texture-modal bigButton keep-text font16 btn btn-link btn-md"
                                        onClick={openBulkTextureModal}>
                                        <UploadXLXS /> <span>Upload with XLSX</span>
                                      </div>
                                    </div>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </PermissionArea>
                            ))
                          }
                          anySelected={anySelected}
                          selectedList={selectedList}
                        />
                        {!useGlobalSearch && (
                          <FilterSideModalButton showTeamMemberFilter={showTeamMemberFilter} />
                        )}
                      </>
                    </div>
                  }
                  searchBar={
                    !useGlobalSearch && (
                      <div className="mt-2">
                        <PageSearchBar
                          model={COMMON_TAGS_MODEL_TYPES.TEXTURE}
                          onSearchChange={onSearchChange}
                          search={search}
                        />
                      </div>
                    )
                  }
                />
              ),
            })}
        item={cardType}
        hasData={list.length > 0 || loading}
        list={list}
        {...(!designV2 &&
          !useGlobalSearch && {
            filterArea: (
              <>
                <ActionFilters
                  loading={loading}
                  sortProps={
                    // (loading || list?.length > 0) &&
                    {
                      value: sortBy,
                      data: sortOptions,
                      onChange: onSortChange,
                    }
                  }
                  selectedProps={
                    list?.length > 0 && {
                      allSelected: allSelected,
                      onSelectAll: () => toggleAll(),
                    }
                  }
                  showTeamMemberFilter={false}
                  membersFilterDefaultSelected={membersFilterDefaultSelected}
                />
                <GlobalFilter />
              </>
            ),
          })}>
        <InfiniteScroll
          dataLength={list.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loading={loading}
          hasChildren={hasData}
          scrollableTarget="infiniteScroll">
          <ThumbnailContainerV2
            designV2={designV2}
            items={list}
            modelType={modelType}
            selected={selected}
            showDownload={true}
            setShowApplyTextureModal={setShowApplyTextureModal}
            cardProps={{
              fullWidth: true,
              onSelect: toggleItem,
              refetchList,
              ...cardProps,
            }}
          />
        </InfiniteScroll>

        {(anySelected || allSelected) && designV2 && (
          <FooterOptions>
            <>
              <div className="selected-item">
                <ActionFilters
                  selectedProps={
                    list?.length > 0 && {
                      allSelected: allSelected,
                      onSelectAll: () => toggleAll(),
                    }
                  }
                />
                <span>{selectedList?.length || 0} Selected</span>
              </div>
              <div className="footer-action">
                <TopButtonArea
                  hasData={hasData}
                  item={cardType}
                  topButtonList={isGuestUser() ? deleteBtn : topButtons}
                  anySelected={anySelected}
                  selectedList={selectedList}
                />
              </div>
            </>
          </FooterOptions>
        )}
      </CommonGrid>

      <AddToCollectionPopup onSuccess={resetSelection} />

      {isGuestUser() ? null : <EditViewTextureDetailsModal />}
      {canAccessV2Page ? (
        <GenerateContentModal
          refetchList={refetchList}
          _showApplyTextureModal={showApplyTextureModal}
          _setShowApplyTextureModal={setShowApplyTextureModal}
        />
      ) : null}
    </>
  );
};

export default MyProductsTextures;

export const EditViewTextureDetailsModal = () => {
  const dispatch = useDispatch();
  const { currentUser } = useUserContext();
  const editData = useSelector(selectTextureDetailsModalData);
  const [searchParams, setSearchParams] = useSearchParams();
  const textureId = searchParams.get('texture_id');

  useEffect(() => {
    dispatch(getCuratorSettings({ userId: currentUser?.user?.id }));
  }, []);

  if (!editData) return null;

  if (editData?.is_map && !editData?.is_mattress_texture) return <TextureDetailsModal />;

  const onClose = () => {
    window.$restrictFilter = null;
    dispatch(textureDetailsModalActions.close());
    if (textureId) {
      searchParams.delete('texture_id');
      setSearchParams(searchParams, { replace: true });
    }
  };

  return <EditTexturePopup data={editData} show={Boolean(editData)} onClose={onClose} />;
};
